import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output
} from '@angular/core';
import * as _ from 'lodash-es';
import {merge} from 'rxjs';
import {filter, switchMap, takeUntil, tap} from 'rxjs/operators';
import {Tab} from '../../models/tab';
import {destroy$} from '../destroyable';
import {LayoutFactory} from '../layout/layout.factory';
import {TabsComponent} from '../tabs/tabs.component';
import {LayoutPreset, LayoutPresetCategory} from './layout-preset';
import {LayoutPresetsService} from './layout-presets.service';

const SELECTOR_VISIBLE_CLASS = '-visible';

const LAYOUT_PRESET_CATEGORY_DICT: {[layoutPresetCategory in LayoutPresetCategory]: string} = {
    [LayoutPresetCategory.BASIC]: 'components.layout-preset-selector.category_basic',
    [LayoutPresetCategory.ADVANCED]: 'components.layout-preset-selector.category_advanced'
};

@Component({
    selector: 'layout-preset-selector',
    templateUrl: './layout-preset-selector.component.html',
    styleUrls: ['./layout-preset-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LayoutPresetSelectorComponent implements OnInit {

    @Input()
    public tabs: TabsComponent;

    @Output()
    public readonly presetApplied = new EventEmitter<Tab>();

    @HostBinding(`class.${SELECTOR_VISIBLE_CLASS}`)
    public isSelectorVisible: boolean = false;

    public currentLayoutPresetCategory: LayoutPresetCategory = LayoutPresetCategory.BASIC;
    public layoutPresetCategories = Object.keys(LayoutPresetCategory) as LayoutPresetCategory[];
    public layoutPresets: LayoutPreset[] = [];

    public readonly layoutPresetCategoryDict = LAYOUT_PRESET_CATEGORY_DICT;
    public readonly destroy$ = destroy$();

    private readonly tabsToDisplaySelectorOn: Tab[] = [];

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private layoutPresetsService: LayoutPresetsService,
        private layoutFactory: LayoutFactory
    ) {}

    public ngOnInit(): void {
        this.listenToTabs();
    }

    public dismissSelector(): void {
        _.pull(this.tabsToDisplaySelectorOn, this.tabs.activeTab);

        this.hideSelector();
    }

    public setLayoutPresetCategory(layoutPresetCategory: LayoutPresetCategory): void {
        this.currentLayoutPresetCategory = layoutPresetCategory;
    }

    public setLayoutPreset(layoutPreset: LayoutPreset): void {
        const tab = this.tabs.activeTab;
        const layoutDef = LayoutFactory.cloneLayoutDefinition(layoutPreset.body, tab.id);

        tab.layout = this.layoutFactory.createLayoutByDefinition(layoutDef);
        this.presetApplied.emit(tab);

        this.dismissSelector();
    }

    private listenToTabs(): void {
        const tabAdded$ = this.tabs.onTabAdded.pipe(
            tap(tab => this.tabsToDisplaySelectorOn.push(tab)),
            tap(() => this.showSelector())
        );
        const tabActivated$ = this.tabs.activeTabChange.pipe(
            tap(tab => this.tabsToDisplaySelectorOn.includes(tab)
                ? this.showSelector()
                : this.hideSelector()
            )
        );

        merge(
            tabAdded$,
            tabActivated$
        ).pipe(
            switchMap(tab => tab.layout.widgetNotification$),
            filter(() => !!this.tabs.activeTab.layout.widgets.length),
            tap(() => this.dismissSelector()),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private showSelector(): void {
        const showSelectorFn = (layoutPresets: LayoutPreset[]) => {
            this.layoutPresets = layoutPresets;
            this.isSelectorVisible = !!layoutPresets.length;
            this.changeDetectorRef.markForCheck();
        };

        this.layoutPresetsService.getAllLayoutPresetsThroughCache().pipe(
            filter(() => !this.tabs.activeTab.layout.widgets.length),
            tap(showSelectorFn),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private hideSelector(): void {
        this.isSelectorVisible = false;
        this.changeDetectorRef.markForCheck();
    }
}
